import type {
  EmailApiRequest,
  EmailAuditMetadata,
  EmailSendResult,
} from "../../types/pdf";
import { supabase } from "../supabase";
import { blobToBase64 } from "../pdf/contractPdfGenerator";
import { downloadPdf, generateContractFilename } from "../pdf/pdfDownloader";

const MANAGEMENT_EMAIL = "verwaltung@teleconnect24.de";
const MIN_DELAY_BETWEEN_SENDS_MS = 600;
const MAX_DELAY_BETWEEN_SENDS_MS = 800;

interface EmailApiFailurePayload {
  success?: false;
  message?: string;
  error?: string;
  code?: number;
  retryable?: boolean;
  attempts?: number;
  elapsedMs?: number;
}

const parseEmailApiResponse = async (
  response: Response
): Promise<EmailApiFailurePayload> => {
  const contentType = response.headers.get("content-type") ?? "";
  const responseText = await response.text();

  if (!responseText) {
    return {};
  }

  if (contentType.toLowerCase().includes("application/json")) {
    try {
      return parseApiFailure(JSON.parse(responseText));
    } catch (error) {
      console.error("Email API returned invalid JSON:", error);
    }
  }

  return {
    success: false,
    message: responseText.slice(0, 500),
  };
};

interface SendSingleEmailApiResult {
  success: boolean;
  error?: string;
  code?: number;
  retryable?: boolean;
  attempts?: number;
  elapsedMs?: number;
}

interface EmailSendJob {
  recipient: string;
  recipientType: "management" | "customer";
  contractType: string;
  pdfBase64: string;
  metadata?: EmailAuditMetadata;
}

interface ContractPdfEmailJob {
  blob: Blob;
  contractType: string;
  metadata?: EmailAuditMetadata;
}

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const getPacingDelayMs = (): number => {
  const range = MAX_DELAY_BETWEEN_SENDS_MS - MIN_DELAY_BETWEEN_SENDS_MS;
  return MIN_DELAY_BETWEEN_SENDS_MS + Math.floor(Math.random() * (range + 1));
};

const parseApiFailure = (result: unknown): EmailApiFailurePayload => {
  if (!result || typeof result !== "object") {
    return {};
  }
  return result as EmailApiFailurePayload;
};

async function sendSingleEmailViaApi(
  pdfBase64: string,
  recipientEmail: string,
  customerName: string,
  emailMetadata?: EmailAuditMetadata
): Promise<SendSingleEmailApiResult> {
  const requestBody: EmailApiRequest = {
    pdfBase64,
    customerEmail: recipientEmail,
    customerName,
    emailMetadata,
  };

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;

    if (!accessToken) {
      return {
        success: false,
        error: "No active session found. Please log in again.",
        code: 401,
        retryable: false,
      };
    }

    const response = await fetch("/api/send-contract-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (response.status === 404) {
      return {
        success: false,
        error:
          "Email API not available - deploy to Vercel to test email sending",
        code: 404,
        retryable: false,
      };
    }

    const payload = await parseEmailApiResponse(response);
    if (response.ok && payload.success !== false) {
      return {
        success: true,
        attempts: payload.attempts,
        elapsedMs: payload.elapsedMs,
      };
    }

    return {
      success: false,
      error: payload.message ?? payload.error ?? "Email send failed",
      code: payload.code ?? response.status,
      retryable: payload.retryable,
      attempts: payload.attempts,
      elapsedMs: payload.elapsedMs,
    };
  } catch (error) {
    console.error("Email API request failed:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Email API request failed unexpectedly",
      retryable: true,
    };
  }
}

/**
 * Sends a single contract PDF via the email API.
 * The API performs provider-aware retry handling.
 */
export async function sendContractEmail(
  pdfBlob: Blob,
  customerEmail: string,
  customerName: string,
  customerLastName: string,
  contractType?: string,
  maxRetries: number = 2,
  emailMetadata?: EmailAuditMetadata
): Promise<EmailSendResult> {
  void maxRetries;

  // Convert PDF blob to base64
  let pdfBase64: string;
  try {
    pdfBase64 = await blobToBase64(pdfBlob);
  } catch (error) {
    console.error("Error converting PDF to base64:", error);
    return {
      success: false,
      error: "Failed to process PDF for sending",
    };
  }

  const result = await sendSingleEmailViaApi(
    pdfBase64,
    customerEmail,
    customerName,
    {
      ...emailMetadata,
      source: emailMetadata?.source ?? "contract_api",
      contractType: emailMetadata?.contractType ?? contractType,
    }
  );
  if (result.success) {
    return {
      success: true,
      attempts: result.attempts,
      elapsedMs: result.elapsedMs,
    };
  }

  console.log("Email send failed. Downloading PDF locally...");
  try {
    const filename = generateContractFilename(customerLastName, contractType);
    downloadPdf(pdfBlob, filename);

    return {
      success: false,
      error: result.error ?? "Email send failed",
      downloadTriggered: true,
      attempts: result.attempts,
      elapsedMs: result.elapsedMs,
    };
  } catch (downloadError) {
    console.error("Error downloading PDF:", downloadError);
    return {
      success: false,
      error: "Failed to send email and download PDF",
      downloadTriggered: false,
      attempts: result.attempts,
      elapsedMs: result.elapsedMs,
    };
  }
}

export async function retryContractEmail(
  pdfBlob: Blob,
  recipientEmail: string,
  customerName: string,
  contractType: string,
  emailMetadata: EmailAuditMetadata
): Promise<EmailSendResult> {
  let pdfBase64: string;
  try {
    pdfBase64 = await blobToBase64(pdfBlob);
  } catch (error) {
    console.error("Error converting retry PDF to base64:", error);
    return {
      success: false,
      error: "Failed to process PDF for retry sending",
    };
  }

  const result = await sendSingleEmailViaApi(
    pdfBase64,
    recipientEmail,
    customerName,
    {
      ...emailMetadata,
      source: "contract_api",
      contractType: emailMetadata.contractType ?? contractType,
    }
  );

  return {
    success: result.success,
    error: result.success ? undefined : result.error ?? "Email retry failed",
    attempts: result.attempts,
    elapsedMs: result.elapsedMs,
  };
}

/**
 * Sends one email per contract type with management-first ordering.
 * Order is deterministic: management jobs for all contracts first, then customer jobs.
 */
export async function sendContractEmailBatchWithManagementPriority(
  pdfs: ContractPdfEmailJob[],
  customerEmail: string,
  customerName: string,
  customerLastName: string,
  onManagementEmailSent?: () => void,
  onCustomerEmailSent?: () => void
): Promise<EmailSendResult> {
  const startedAt = Date.now();
  const hasCustomerEmail = hasValidCustomerEmail(customerEmail);

  if (pdfs.length === 0) {
    return {
      success: false,
      error: "No PDFs available for email sending",
      failedJobs: [],
      elapsedMs: 0,
    };
  }

  const preparedPdfs: {
    blob: Blob;
    contractType: string;
    base64: string;
    metadata?: EmailAuditMetadata;
  }[] = [];
  try {
    for (const pdf of pdfs) {
      const base64 = await blobToBase64(pdf.blob);
      preparedPdfs.push({
        blob: pdf.blob,
        contractType: pdf.contractType,
        base64,
        metadata: pdf.metadata,
      });
    }
  } catch (error) {
    console.error("Error converting PDFs to base64:", error);
    return {
      success: false,
      error: "Failed to process PDFs for sending",
      failedJobs: [],
      elapsedMs: Date.now() - startedAt,
    };
  }

  const buildJobMetadata = (
    baseMetadata: EmailAuditMetadata | undefined,
    contractType: string,
    recipientType: "management" | "customer"
  ): EmailAuditMetadata => ({
    ...baseMetadata,
    source: baseMetadata?.source ?? "contract_api",
    contractType: baseMetadata?.contractType ?? contractType,
    recipientType,
  });

  const jobs: EmailSendJob[] = [
    ...preparedPdfs.map((pdf) => ({
      recipient: MANAGEMENT_EMAIL,
      recipientType: "management" as const,
      contractType: pdf.contractType,
      pdfBase64: pdf.base64,
      metadata: buildJobMetadata(pdf.metadata, pdf.contractType, "management"),
    })),
    ...(hasCustomerEmail
      ? preparedPdfs.map((pdf) => ({
          recipient: customerEmail,
          recipientType: "customer" as const,
          contractType: pdf.contractType,
          pdfBase64: pdf.base64,
          metadata: buildJobMetadata(
            pdf.metadata,
            pdf.contractType,
            "customer"
          ),
        }))
      : []),
  ];

  console.log("Starting contract email batch", {
    totalJobs: jobs.length,
    managementJobs: preparedPdfs.length,
    customerJobs: hasCustomerEmail ? preparedPdfs.length : 0,
    order: "management-first",
  });

  let managementSentCount = 0;
  let customerSentCount = 0;
  let attempts = 0;
  let totalApiElapsedMs = 0;
  const failedJobs: {
    recipient: string;
    contractType: string;
    reason: string;
  }[] = [];

  for (let index = 0; index < jobs.length; index++) {
    const job = jobs[index];
    if (!job) {
      continue;
    }

    if (index > 0) {
      const delayMs = getPacingDelayMs();
      console.log("Applying pacing delay before next email", {
        delayMs,
        jobIndex: index + 1,
        totalJobs: jobs.length,
      });
      await sleep(delayMs);
    }

    console.log("Sending contract email job", {
      recipientType: job.recipientType,
      contractType: job.contractType,
      jobIndex: index + 1,
      totalJobs: jobs.length,
    });

    const result = await sendSingleEmailViaApi(
      job.pdfBase64,
      job.recipient,
      customerName,
      job.metadata
    );

    attempts += result.attempts ?? 1;
    totalApiElapsedMs += result.elapsedMs ?? 0;

    if (result.success) {
      if (job.recipientType === "management") {
        managementSentCount++;
        onManagementEmailSent?.();
      } else {
        customerSentCount++;
        onCustomerEmailSent?.();
      }
      continue;
    }

    failedJobs.push({
      recipient: job.recipient,
      contractType: job.contractType,
      reason: result.error ?? "Email send failed",
    });
  }

  const managementTargetCount = preparedPdfs.length;
  const customerTargetCount = hasCustomerEmail ? preparedPdfs.length : 0;
  const managementEmailSent =
    managementTargetCount > 0 && managementSentCount === managementTargetCount;
  const customerEmailSent =
    customerTargetCount > 0 && customerSentCount === customerTargetCount;

  let downloadTriggered = false;
  const shouldDownloadLocally = !hasCustomerEmail || failedJobs.length > 0;
  if (shouldDownloadLocally) {
    try {
      for (const pdf of preparedPdfs) {
        const filename = generateContractFilename(
          customerLastName,
          pdf.contractType
        );
        downloadPdf(pdf.blob, filename);
      }
      downloadTriggered = true;
    } catch (downloadError) {
      console.error("Error downloading PDFs:", downloadError);
    }
  }

  const success = hasCustomerEmail
    ? managementEmailSent && customerEmailSent
    : managementEmailSent;

  console.log("Completed contract email batch", {
    success,
    managementSentCount,
    managementTargetCount,
    customerSentCount,
    customerTargetCount,
    failedJobs: failedJobs.length,
    downloadTriggered,
  });

  return {
    success,
    managementEmailSent,
    customerEmailSent,
    customerEmailMissing: !hasCustomerEmail,
    managementSentCount,
    customerSentCount,
    failedJobs,
    downloadTriggered,
    error:
      failedJobs.length > 0 ? "One or more emails failed to send" : undefined,
    attempts,
    elapsedMs: Math.max(Date.now() - startedAt, totalApiElapsedMs),
  };
}

/**
 * Backward-compatible wrapper for single-contract flow.
 */
export async function sendContractEmailsWithManagementCopy(
  pdfBlob: Blob,
  customerEmail: string,
  customerName: string,
  customerLastName: string,
  contractType?: string,
  emailMetadata?: EmailAuditMetadata,
  onManagementEmailSent?: () => void,
  onCustomerEmailSent?: () => void
): Promise<EmailSendResult> {
  const result = await sendContractEmailBatchWithManagementPriority(
    [
      {
        blob: pdfBlob,
        contractType: contractType ?? "Energievertrag",
        metadata: emailMetadata,
      },
    ],
    customerEmail,
    customerName,
    customerLastName
  );

  if (result.managementEmailSent) {
    onManagementEmailSent?.();
  }
  if (result.customerEmailSent) {
    onCustomerEmailSent?.();
  }

  return result;
}

/**
 * Backward-compatible wrapper for legacy multi-contract API.
 * @deprecated Use sendContractEmailBatchWithManagementPriority.
 */
export async function sendMultipleContractEmailsWithManagementCopy(
  pdfs: ContractPdfEmailJob[],
  customerEmail: string,
  customerName: string,
  customerLastName: string,
  maxRetries: number = 2,
  onManagementEmailSent?: () => void,
  onCustomerEmailSent?: () => void
): Promise<EmailSendResult> {
  void maxRetries;
  console.warn(
    "sendMultipleContractEmailsWithManagementCopy is deprecated. Use sendContractEmailBatchWithManagementPriority instead."
  );

  const result = await sendContractEmailBatchWithManagementPriority(
    pdfs,
    customerEmail,
    customerName,
    customerLastName
  );

  if (result.managementEmailSent) {
    onManagementEmailSent?.();
  }
  if (result.customerEmailSent) {
    onCustomerEmailSent?.();
  }

  return result;
}

/**
 * Validates email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Checks if customer has a valid email address
 * Returns false if email is empty, whitespace only, or invalid format
 */
export function hasValidCustomerEmail(email: string): boolean {
  return !!email && email.trim() !== "" && isValidEmail(email);
}
